/**
 * The form of a manual order, from the API (GET api/order/dispatch/form).
 *
 * The API owns the billing rule of a restaurant's quick order, and the
 * sentence that tells the restaurant about it. The office changed that rule
 * two times in five weeks (api#143 on 2026-09-06, no commission on
 * 2026-10-13). A sentence that this dashboard or the tablet writes by hand is
 * wrong after the next change.
 *
 * The key has the account id: an admin who impersonates a restaurant gets the
 * form of that restaurant, not the one of the last account.
 */
import { useQuery } from "@tanstack/react-query";
import { getDispatchOrderForm } from "services/order";

/** Query key factory — keeps cache keys consistent. */
export const dispatchOrderFormKeys = {
    all: ["dispatch-order-form"],
    forUser: (userId) => [...dispatchOrderFormKeys.all, userId],
};

/**
 * @param {object} params
 * @param {number|undefined} params.userId - The signed-in account.
 * @param {boolean} [params.enabled=true] - Read only while the form is open.
 * @returns {{form: {countsInStatement: boolean, hints: {total?: string}} | null}}
 *   null while it loads, and when the read fails: the caller then shows its
 *   own neutral sentence.
 */
const useDispatchOrderForm = ({ userId, enabled = true }) => {
    const query = useQuery({
        queryKey: dispatchOrderFormKeys.forUser(userId),
        queryFn: getDispatchOrderForm,
        enabled: enabled && Boolean(userId),
        staleTime: 5 * 60 * 1000,
    });

    return { form: query.data ?? null };
};

export default useDispatchOrderForm;
